import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { MapPin, Phone, Zap, Calendar, Check, ChevronRight, Lock } from 'lucide-react';
import TechPhotos from './TechPhotos';
import TechCodesBarres from './TechCodesBarres';
import TechRaccordement from './TechRaccordement';
import TechConfigApp from './TechConfigApp';
import TechChantierTermine from './TechChantierTermine';
import SignatureDocument from './SignatureDocument';
import BoussoleToiture from './BoussoleToiture';
import { listerEnAttente, surChangement } from '../utils/fileAttentePhotos';
import { ajouterEtape, listerEnAttente as etapesEnAttente } from '../utils/fileAttenteEtapes';

const isoLocal = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const API = process.env.REACT_APP_BACKEND_URL || '';

const ETAPES = [
  { cle: 'arrivee', label: 'Je suis arrivé sur le chantier' },
  { cle: 'previsite', label: 'Prévisite terminée', enCours: 'Prévisite en cours' },
  { cle: 'installation', label: "Démarrer l'installation", enCours: "Installation" },
  { cle: 'codes_barres', label: 'Codes-barres et raccordement terminés', enCours: 'Codes-barres et raccordement en cours' },
  { cle: 'config_app', label: 'Application configurée', enCours: 'Application en cours de configuration' },
  { cle: 'photos_apres', label: 'Photos après chantier terminées', enCours: 'Photos après chantier' },
  { cle: 'termine', label: 'Chantier terminé' }
];

const ORDRE = ['planifie', ...ETAPES.map((e) => e.cle)];
const ORDRE_LIB = ETAPES.reduce((a, e) => ({ ...a, [e.cle]: e.label }), { planifie: 'Chantier planifié' });

// Etape que le technicien s'apprete a valider (la suivante dans l'ordre)
const etapeAVenir = (courante) => {
  const i = ORDRE.indexOf(courante);
  return ETAPES[i] ? ETAPES[i].cle : null;
};

// Un chantier s'ouvre a partir de la veille, ou s'il est deja demarre / termine
const formaterJour = (iso) => {
  if (!iso) return '—';
  const [a, m, j] = String(iso).split('-');
  return `${j}/${m}/${a}`;
};

const estAccessible = (c) => {
  if (!c) return false;
  if (c.etape && c.etape !== 'planifie') return true;
  if (!c.date_prevue) return false;
  const debut = new Date(c.date_prevue + 'T00:00:00');
  const veille = new Date(debut);
  veille.setDate(veille.getDate() - 1);
  const maintenant = new Date();
  maintenant.setHours(0, 0, 0, 0);
  return maintenant >= veille;
};

const TechMesChantiers = () => {
  const [chantiers, setChantiers] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [ouvert, setOuvert] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const [typesPhotos, setTypesPhotos] = useState({});
  const location = useLocation();
  const [attente, setAttente] = useState([]);
  const [histoOuvert, setHistoOuvert] = useState(false);
  const [onglet, setOnglet] = useState('actifs');
  const [docsSignes, setDocsSignes] = useState([]);
  const [etatEtape, setEtatEtape] = useState({});
  const [fete, setFete] = useState(false);
  const navigate = useNavigate();

  // La file d'attente est relue quand le technicien agit, pas en continu :
  // un rafraichissement automatique refermerait les fenetres ouvertes.
  const relireAttente = async () => setAttente(await listerEnAttente());

  useEffect(() => { relireAttente(); }, []);

  // Reapplique les etapes pas encore confirmees par le serveur
  const fusionner = async (liste) => {
    const attente = await etapesEnAttente();
    if (!attente.length) return liste;
    return liste.map((c) => {
      const derniere = attente.filter((a) => a.chantierId === c.id).pop();
      return derniere ? { ...c, etape: derniere.etape } : c;
    });
  };

  const charger = async () => {
    try {
      const [r, rt] = await Promise.all([
        axios.get(`${API}/api/chantiers/mes/liste`),
        axios.get(`${API}/api/photos/types`)
      ]);
      const liste = r.data.data || [];
      setChantiers(await fusionner(liste));
      setTypesPhotos(rt.data.data || {});

      // Ouverture directe si on arrive depuis l'agenda
      const vise = location.state?.chantierId;
      const viseProspect = location.state?.prospectId;
      if (vise || viseProspect) {
        const trouve = liste.find((c) => c.id === vise || c.prospect_id === viseProspect);
        if (trouve && estAccessible(trouve)) setOuvert(trouve);
        else if (trouve) setErreur('Ce chantier sera accessible la veille de l\'installation.');
      }
    } catch (e) {
      setErreur('Impossible de charger vos chantiers');
    } finally {
      setChargement(false);
    }
  };

  useEffect(() => {
    charger();
    const t = setInterval(charger, 120000);
    const auRetour = () => { if (!document.hidden) charger(); };
    document.addEventListener('visibilitychange', auRetour);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', auRetour); };
  }, []);

  const rafraichir = async () => {
    try {
      const r = await axios.get(`${API}/api/chantiers/mes/liste`);
      const liste = await fusionner(r.data.data || []);
      setChantiers(liste);
      if (ouvert) {
        const maj = liste.find((c) => c.id === ouvert.id);
        if (maj) setOuvert(maj);
      }
    } catch (e) {}
  };

  const avancer = (chantierId, etape) => {
    setErreur('');

    // L'ecran avance tout de suite
    const maj = (c) => (c.id === chantierId ? { ...c, etape } : c);
    setChantiers((liste) => liste.map(maj));
    if (ouvert?.id === chantierId) setOuvert((o) => ({ ...o, etape }));

    // L'envoi part en arriere-plan, avec reessai automatique
    ajouterEtape(chantierId, etape);

    if (etape === 'termine') setFete(true);
  };

  const carte = { background: '#fff', borderRadius: '14px', padding: '18px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: '14px' };

  if (chargement) return <div style={{ padding: '24px', textAlign: 'center', color: '#6B7280' }}>Chargement…</div>;

  // Vue detail d'un chantier
  if (fete) {
    return <TechChantierTermine nbEtapes={ETAPES.length} onFini={() => { setFete(false); navigate('/ops/tech'); }} />;
  }

  if (ouvert) {
    const idx = ORDRE.indexOf(ouvert.etape);
    const clos = ouvert.etape === 'termine';
    const jourJ = ouvert.date_prevue === isoLocal(new Date()) || (ouvert.etape !== 'planifie' && !clos);
    return (
      <div style={{ padding: '8px', paddingBottom: '100px', maxWidth: '640px', margin: '0 auto', width: '100%' }}>
        <div onClick={() => setOuvert(null)} style={{ color: '#10B981', fontSize: '14px', marginBottom: '14px', cursor: 'pointer' }}>
          ← Retour à mes chantiers
        </div>

        {erreur && <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '12px', borderRadius: '8px', marginBottom: '14px' }}>{erreur}</div>}

        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '18px', fontWeight: 600 }}>
            {ouvert.client ? `${ouvert.client.prenom} ${ouvert.client.nom}` : 'Chantier'}
          </div>
          {ouvert.commentaire && (
            <div style={{ marginTop: '10px', padding: '12px', background: '#FFFBEB', borderRadius: '8px', fontSize: '14px', color: '#92400E' }}>
              {ouvert.commentaire}
            </div>
          )}
        </div>

        {!clos && (
        <div style={carte}>
          {(() => {
            const rang = idx;
            const total = ETAPES.length;
            const suivante = ETAPES[rang];
            const apres = ETAPES[rang + 1];
            const requis = typesPhotos[etapeAVenir(ouvert.etape)] || [];
            const manquantes = requis.filter((t) =>
              !(ouvert.photos || []).some((p) => p.type === t.cle) &&
              !attente.some((p) => p.chantierId === ouvert.id && p.typePhoto === t.cle)
            );
            const bordereauRequis = ouvert.etape === 'arrivee';
            const pvRequis = ouvert.etape === 'config_app';
            const signatures = (ouvert.documents || []).map((x) => x.type);
            const manqueSignature =
              (bordereauRequis && !signatures.includes('bordereau')) ||
              (pvRequis && !signatures.includes('pv_reception'));
            const manqueOrientation = bordereauRequis && !ouvert.orientation_toiture;
            const bloque = manquantes.length > 0 || manqueSignature || manqueOrientation;

            const pct = Math.min(rang / total, 1);
            const circo = 2 * Math.PI * 33;

            if (!suivante) return null;

            const traceCourante = [...(ouvert.journal || [])].reverse().find((j) => j.etape === ouvert.etape);
            const heure = traceCourante?.date
              ? new Date(traceCourante.date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
              : null;

            return (
              <>
                <div style={{ textAlign: 'center', marginBottom: '22px' }}>
                  <div style={{ position: 'relative', width: '76px', height: '76px', margin: '0 auto 16px' }}>
                    <svg viewBox="0 0 76 76" style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
                      <circle cx="38" cy="38" r="33" fill="none" stroke="#ECFDF5" strokeWidth="7" />
                      <circle cx="38" cy="38" r="33" fill="none" stroke="#10B981" strokeWidth="7" strokeLinecap="round"
                        strokeDasharray={circo} strokeDashoffset={circo * (1 - pct)}
                        style={{ transition: 'stroke-dashoffset .4s' }} />
                    </svg>
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: '22px', fontWeight: 600, lineHeight: 1 }}>{rang + 1}</span>
                      <span style={{ fontSize: '11px', color: '#9CA3AF' }}>sur {total}</span>
                    </div>
                  </div>

                  <div style={{ fontSize: '21px', fontWeight: 600, marginBottom: '6px' }}>{suivante.enCours || suivante.label}</div>
                  {heure && ouvert.etape !== 'planifie' && (
                    <div style={{ fontSize: '14px', color: '#6B7280' }}>
                      {ORDRE_LIB[ouvert.etape] || ouvert.etape_libelle} à {heure}
                    </div>
                  )}
                </div>

                {!bordereauRequis && !pvRequis && ouvert.etape !== 'installation' && ouvert.etape !== 'codes_barres' && (
                <button disabled={enCours || bloque || !jourJ}
                  onClick={() => avancer(ouvert.id, suivante.cle)}
                  style={{
                    width: '100%', padding: '16px', border: 'none', borderRadius: '10px',
                    background: (bloque || !jourJ) ? '#F3F4F6' : '#10B981', color: (bloque || !jourJ) ? '#9CA3AF' : '#fff',
                    fontSize: '17px', cursor: (bloque || !jourJ) ? 'default' : 'pointer', marginBottom: '14px'
                  }}>
                  {enCours ? 'Enregistrement…' : !jourJ ? 'Disponible le jour du chantier' : manquantes.length > 0 ? `${manquantes.length} photo(s) manquante(s)` : manqueOrientation ? 'Orientation à mesurer' : manqueSignature ? 'Signature du client requise' : (suivante ? (suivante.cle === 'termine' ? 'Chantier terminé' : suivante.label) : 'Valider cette étape')}
                </button>
                )}

                {apres && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', fontSize: '13px', color: '#9CA3AF', marginBottom: '16px' }}>
                    <span>Ensuite : {apres.label}</span>
                    <ChevronRight size={15} />
                  </div>
                )}

                {rang > 0 && (
                  <div onClick={() => setHistoOuvert(!histoOuvert)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', paddingTop: '14px', borderTop: '1px solid #F3F4F6', cursor: 'pointer' }}>
                    <ChevronRight size={15} color="#6B7280" style={{ transform: histoOuvert ? 'rotate(90deg)' : 'rotate(180deg)', transition: 'transform .2s' }} />
                    <span style={{ fontSize: '14px', color: '#6B7280' }}>
                      {histoOuvert ? 'Masquer les étapes précédentes' : 'Voir les étapes précédentes'}
                    </span>
                  </div>
                )}

                {histoOuvert && (
                  <div style={{ marginTop: '14px' }}>
                    {ETAPES.slice(0, rang).map((e, i) => {
                      const tr = [...(ouvert.journal || [])].reverse().find((j) => j.etape === e.cle);
                      return (
                        <div key={e.cle} style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '9px 0', borderTop: i > 0 ? '1px solid #F9FAFB' : 'none' }}>
                          <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#10B981', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Check size={12} color="#fff" />
                          </span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '14px', color: '#374151' }}>{e.label}</div>
                            {tr?.date && (
                              <div style={{ fontSize: '11px', color: '#9CA3AF' }}>
                                {new Date(tr.date).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                {tr.par ? ` · ${tr.par}` : ''}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            );
          })()}
        </div>
        )}

        {clos && (
          <>
            <div style={carte}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '14px' }}>
                <Check size={20} color="#10B981" strokeWidth={2.6} />
                <span style={{ fontSize: '16px', fontWeight: 500 }}>Chantier terminé</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px' }}>
                {[
                  ['Puissance', ouvert.puissance_kwc ? `${ouvert.puissance_kwc} kWc` : '—'],
                  ['Panneaux', ouvert.nb_panneaux || '—'],
                  ['Orientation', (ouvert.orientation_toiture || {}).libelle || '—'],
                  ['Durée', ouvert.duree_jours ? `${ouvert.duree_jours} jour${ouvert.duree_jours > 1 ? 's' : ''}` : '—'],
                ].map(([lib, val]) => (
                  <div key={lib} style={{ background: '#F9FAFB', borderRadius: '9px', padding: '12px' }}>
                    <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '3px' }}>{lib}</div>
                    <div style={{ fontSize: '18px', color: lib === 'Orientation' ? '#065F46' : '#111827' }}>{val}</div>
                  </div>
                ))}
              </div>
            </div>

            {(ouvert.codes_barres || []).length > 0 && (
              <div style={carte}>
                <div style={{ fontSize: '15px', fontWeight: 500, marginBottom: '12px' }}>Micro-onduleurs et passerelle</div>
                {(ouvert.codes_barres || []).map((c, i) => {
                  const liaisons = (ouvert.raccordement || {}).liaisons || {};
                  const pans = liaisons[c.numero] || [];
                  const estMicro = c.type_materiel === 'micro_onduleur';
                  return (
                    <div key={c.id || i} style={{ padding: '10px 0', borderTop: '1px solid #F3F4F6' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '10px' }}>
                        <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>N°{c.numero}</span>
                        <span style={{ fontSize: '11.5px', color: '#6B7280', flexShrink: 0 }}>
                          {estMicro ? 'Micro-onduleur' : 'Passerelle'}
                        </span>
                      </div>
                      {pans.length > 0 && (
                        <div style={{ fontSize: '11.5px', color: '#065F46', marginTop: '3px' }}>
                          Panneau{pans.length > 1 ? 'x' : ''} {pans.join(' et ')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {(ouvert.documents || []).length > 0 && (
              <div style={carte}>
                <div style={{ fontSize: '15px', fontWeight: 500, marginBottom: '12px' }}>Documents signés</div>
                {(ouvert.documents || []).map((d) => (
                  <div key={d.id} style={{ padding: '10px 0', borderTop: '1px solid #F3F4F6' }}>
                    <SignatureDocument chantierId={ouvert.id} typeDocument={d.type} />
                  </div>
                ))}
              </div>
            )}

            {['previsite', 'codes_barres', 'photos_apres'].map((et) => {
              const types = typesPhotos[et];
              if (!types) return null;
              const titre = et === 'previsite' ? 'Photos avant chantier'
                : et === 'codes_barres' ? 'Photos micro-onduleurs et passerelle'
                : 'Photos après chantier';
              const dedans = (ouvert.photos || []).filter((p) => types.some((t) => t.cle === p.type));
              if (dedans.length === 0) return null;
              return (
                <div key={et} style={carte}>
                  <div style={{ fontSize: '15px', fontWeight: 500, marginBottom: '14px' }}>{titre}</div>
                  <TechPhotos chantierId={ouvert.id} photos={ouvert.photos || []} types={types}
                    onChange={() => { rafraichir(); relireAttente(); }} verrouille={true} />
                </div>
              );
            })}
          </>
        )}

        {typesPhotos[etapeAVenir(ouvert.etape)] && (
          <div style={carte}>
            <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>Photos à prendre</div>
            <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '14px' }}>
              Toutes les photos sont obligatoires pour passer à l'étape suivante.
            </div>
            <TechPhotos
              chantierId={ouvert.id}
              photos={ouvert.photos || []}
              types={typesPhotos[etapeAVenir(ouvert.etape)]}
              nbMicros={(() => {
                const n = ouvert.nb_panneaux || 0;
                const pm = ouvert.panneaux_par_micro || 2;
                return n ? Math.ceil(n / pm) : 0;
              })()}
              onChange={() => { rafraichir(); relireAttente(); }}
              verrouille={false}
            />
          </div>
        )}

        {ouvert.etape === 'arrivee' && (() => {
          const req = typesPhotos[etapeAVenir(ouvert.etape)] || [];
          const manq = req.filter((t) =>
            !(ouvert.photos || []).some((ph) => ph.type === t.cle) &&
            !attente.some((ph) => ph.chantierId === ouvert.id && ph.typePhoto === t.cle)
          );
          if (manq.length > 0) {
            return (
              <div style={{ background: '#fff', border: '1px solid #F3F4F6', borderRadius: '12px', padding: '17px', marginBottom: '14px', opacity: 0.55 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '6px' }}>
                  <Lock size={16} color="#9CA3AF" />
                  <span style={{ fontSize: '15px', fontWeight: 500, color: '#6B7280' }}>Orientation de la toiture</span>
                </div>
                <div style={{ fontSize: '13px', color: '#9CA3AF' }}>
                  Disponible une fois les photos prises
                </div>
              </div>
            );
          }
          return (
          <BoussoleToiture
            chantierId={ouvert.id}
            orientation={ouvert.orientation_toiture}
            onEnregistre={rafraichir} />
          );
        })()}

        {(ouvert.etape === 'arrivee' || ouvert.etape === 'config_app') && (
          <>
            {(() => {
              const req2 = typesPhotos[etapeAVenir(ouvert.etape)] || [];
              const manq2 = req2.filter((t) =>
                !(ouvert.photos || []).some((ph) => ph.type === t.cle) &&
                !attente.some((ph) => ph.chantierId === ouvert.id && ph.typePhoto === t.cle)
              );
              const orientationOk = ouvert.etape !== 'arrivee' || !!ouvert.orientation_toiture;
              const pret = manq2.length === 0 && orientationOk;

              if (!pret) {
                return (
                  <div style={{ background: '#fff', border: '1px solid #F3F4F6', borderRadius: '12px', padding: '17px', marginBottom: '14px', opacity: 0.55 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '6px' }}>
                      <Lock size={16} color="#9CA3AF" />
                      <span style={{ fontSize: '15px', fontWeight: 500, color: '#6B7280' }}>
                        {ouvert.etape === 'arrivee' ? "Bordereau d'exécution" : 'Procès-verbal de réception'}
                      </span>
                    </div>
                    <div style={{ fontSize: '13px', color: '#9CA3AF' }}>
                      {manq2.length > 0 ? 'Disponible une fois les photos prises' : "Disponible une fois l'orientation mesurée"}
                    </div>
                  </div>
                );
              }

              return (
                <div style={{ marginBottom: '14px' }}>
                  <SignatureDocument
                    chantierId={ouvert.id}
                    typeDocument={ouvert.etape === 'arrivee' ? 'bordereau' : 'pv_reception'}
                    orientation={ouvert.orientation_toiture}
                    onSigne={rafraichir} />
                </div>
              );
            })()}

            {(() => {
              const rg = ORDRE.indexOf(ouvert.etape);
              const suiv = ETAPES[rg];
              if (!suiv) return null;
              const jj = ouvert.date_prevue === isoLocal(new Date()) || (ouvert.etape !== 'planifie' && ouvert.etape !== 'termine');

              const req = typesPhotos[etapeAVenir(ouvert.etape)] || [];
              const manq = req.filter((t) =>
                !(ouvert.photos || []).some((ph) => ph.type === t.cle) &&
                !attente.some((ph) => ph.chantierId === ouvert.id && ph.typePhoto === t.cle)
              );
              const typeDoc = ouvert.etape === 'arrivee' ? 'bordereau' : 'pv_reception';
              const signe = (ouvert.documents || []).some((x) => x.type === typeDoc);
              const bl = manq.length > 0 || !signe;

              return (
                <button disabled={enCours || bl || !jj}
                  onClick={() => avancer(ouvert.id, suiv.cle)}
                  style={{
                    width: '100%', padding: '16px', border: 'none', borderRadius: '10px',
                    background: (bl || !jj) ? '#F3F4F6' : '#10B981',
                    color: (bl || !jj) ? '#9CA3AF' : '#fff',
                    fontSize: '17px', cursor: (bl || !jj) ? 'default' : 'pointer', marginBottom: '14px'
                  }}>
                  {enCours ? 'Enregistrement…'
                    : !jj ? 'Disponible le jour du chantier'
                    : manq.length > 0 ? `${manq.length} photo(s) manquante(s)`
                    : !signe ? 'Signature du client requise'
                    : (suiv ? (suiv.cle === 'termine' ? 'Chantier terminé' : suiv.label) : 'Valider cette étape')}
                </button>
              );
            })()}
          </>
        )}

        {ouvert.etape === 'codes_barres' && (
          <div style={carte}>
            <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '10px' }}>Configuration APSystems</div>
            <TechConfigApp chantierId={ouvert.id} />

          </div>
        )}

          {ouvert.etape === 'codes_barres' && (() => {
            const rg = ORDRE.indexOf(ouvert.etape);
            const suiv = ETAPES[rg];
            if (!suiv) return null;
            const jj = ouvert.date_prevue === isoLocal(new Date()) || (ouvert.etape !== 'planifie' && ouvert.etape !== 'termine');
            return (
              <button disabled={enCours || !jj}
                onClick={() => avancer(ouvert.id, suiv.cle)}
                style={{
                  width: '100%', marginTop: '6px', padding: '16px', border: 'none', borderRadius: '10px',
                  background: !jj ? '#F3F4F6' : '#10B981',
                  color: !jj ? '#9CA3AF' : '#fff',
                  fontSize: '17px', cursor: !jj ? 'default' : 'pointer'
                }}>
                {enCours ? 'Enregistrement…' : !jj ? 'Disponible le jour du chantier' : (suiv ? (suiv.cle === 'termine' ? 'Chantier terminé' : suiv.label) : 'Valider cette étape')}
              </button>
            );
          })()}

        {ouvert.etape === 'installation' && (
          <div style={carte}>
            <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>Numéros de série</div>
            <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '14px' }}>
              Scannez ou saisissez les numéros des micro-onduleurs et de la passerelle.
            </div>
            <TechCodesBarres chantierId={ouvert.id} verrouille={false} onChange={rafraichir}
              bloque={(() => {
                const req = typesPhotos[etapeAVenir(ouvert.etape)] || [];
                return req.some((t) =>
                  !(ouvert.photos || []).some((ph) => ph.type === t.cle) &&
                  !attente.some((ph) => ph.chantierId === ouvert.id && ph.typePhoto === t.cle)
                );
              })()}
              messageBloque="Disponible une fois les photos prises" />

            <div style={{ marginTop: '22px', paddingTop: '18px', borderTop: '1px solid #F3F4F6' }}>
              <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>Raccordement</div>
              <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '16px' }}>
                Associez chaque micro-onduleur à ses panneaux.
              </div>
              <TechRaccordement chantierId={ouvert.id} onEnregistre={rafraichir} cleMaj={(ouvert.codes_barres || []).length}
                bloque={(() => {
                  const codes = ouvert.codes_barres || [];
                  const micros = codes.filter((c) => c.type_materiel === 'micro_onduleur').length;
                  const passerelles = codes.filter((c) => c.type_materiel === 'passerelle').length;
                  const n = ouvert.nb_panneaux || 0;
                  const pm = ouvert.panneaux_par_micro || 2;
                  const attendus = n ? Math.ceil(n / pm) : 0;
                  return micros < attendus || passerelles < 1;
                })()}
                messageBloque="Disponible une fois les numéros de série scannés" />
            </div>

          </div>
        )}

          {ouvert.etape === 'installation' && (() => {
            const rg = ORDRE.indexOf(ouvert.etape);
            const suiv = ETAPES[rg];
            if (!suiv) return null;
            const jj = ouvert.date_prevue === isoLocal(new Date()) || (ouvert.etape !== 'planifie' && ouvert.etape !== 'termine');
            const n = ouvert.nb_panneaux || 0;
            const pm = ouvert.panneaux_par_micro || 2;
            const attendus = n ? Math.ceil(n / pm) : 0;

            // Photos : le nombre attendu depend du type
            const req = typesPhotos[etapeAVenir(ouvert.etape)] || [];
            const prises = (cle) =>
              (ouvert.photos || []).filter((ph) => ph.type === cle).length +
              attente.filter((ph) => ph.chantierId === ouvert.id && ph.typePhoto === cle).length;

            // On n'annonce que la premiere chose qui manque, dans l'ordre
            let etapeManquante = null;
            for (const t of req) {
              const vise = t.cle === 'micro_onduleur' ? attendus : 1;
              const reste = Math.max(0, vise - prises(t.cle));
              if (reste > 0) {
                etapeManquante = `${reste} photo${reste > 1 ? 's' : ''} · ${t.label}`;
                break;
              }
            }
            const manqPhotos = etapeManquante ? 1 : 0;

            // Numeros de serie
            const codes = ouvert.codes_barres || [];
            const nbMicros = codes.filter((c) => c.type_materiel === 'micro_onduleur').length;
            const nbPass = codes.filter((c) => c.type_materiel === 'passerelle').length;
            const resteMicros = Math.max(0, attendus - nbMicros);
            const texteNumeros = resteMicros > 0
              ? `${resteMicros} micro-onduleur${resteMicros > 1 ? 's' : ''} à scanner`
              : (nbPass < 1 ? 'Passerelle à scanner' : null);
            const manqNumeros = texteNumeros !== null;

            // Raccordement
            const liaisons = (ouvert.raccordement || {}).liaisons || {};
            const raccordes = Object.keys(liaisons).filter((k) => (liaisons[k] || []).length).length;
            const manqRaccord = raccordes < attendus;

            const bl = manqPhotos > 0 || manqNumeros || manqRaccord;

            return (
              <button disabled={enCours || bl || !jj}
                onClick={() => avancer(ouvert.id, suiv.cle)}
                style={{
                  width: '100%', marginTop: '18px', padding: '16px', border: 'none', borderRadius: '10px',
                  background: (bl || !jj) ? '#F3F4F6' : '#10B981',
                  color: (bl || !jj) ? '#9CA3AF' : '#fff',
                  fontSize: '17px', cursor: (bl || !jj) ? 'default' : 'pointer'
                }}>
                {enCours ? 'Enregistrement…'
                  : !jj ? 'Disponible le jour du chantier'
                  : etapeManquante ? etapeManquante
                  : texteNumeros ? texteNumeros
                  : manqRaccord ? 'Raccordement à terminer'
                  : (suiv ? (suiv.cle === 'termine' ? 'Chantier terminé' : suiv.label) : 'Valider cette étape')}
              </button>
            );
          })()}

      </div>
    );
  }

  // Liste des chantiers
  const HORAIRES_LIB = { journee: '8h30 – 19h00', matin: '8h30 – 13h00', apres_midi: '14h30 – 19h00' };
  const auj = isoLocal(new Date());

  const dateLongue = (iso) => {
    if (!iso) return '';
    const d = new Date(iso + 'T12:00:00');
    return isNaN(d) ? iso : d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const dateCourte = (iso) => {
    if (!iso) return '';
    const d = new Date(iso + 'T12:00:00');
    return isNaN(d) ? iso : d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }).toUpperCase();
  };

  const veilleDe = (iso) => {
    if (!iso) return '';
    const d = new Date(iso + 'T12:00:00');
    d.setDate(d.getDate() - 1);
    return dateLongue(isoLocal(d));
  };

  const actifs = chantiers.filter((c) => c.etape !== 'termine')
    .sort((a, b) => (a.date_prevue || '').localeCompare(b.date_prevue || ''));
  const termines = chantiers.filter((c) => c.etape === 'termine')
    .sort((a, b) => (b.date_prevue || '').localeCompare(a.date_prevue || ''));
  const liste = onglet === 'actifs' ? actifs : termines;

  const ongletBtn = (actif) => ({
    flex: 1, padding: '8px', border: 'none', borderRadius: '7px', fontSize: '13.5px', cursor: 'pointer',
    background: actif ? '#fff' : 'transparent',
    color: actif ? '#111827' : '#9CA3AF',
    boxShadow: actif ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
  });


  return (
    <div style={{ padding: '8px', paddingBottom: '100px', maxWidth: '640px', margin: '0 auto', width: '100%' }}>
      <style>{`@keyframes pulseJalon {
        0% { box-shadow: 0 0 0 0 rgba(16,185,129,0.30); }
        70% { box-shadow: 0 0 0 7px rgba(16,185,129,0); }
        100% { box-shadow: 0 0 0 0 rgba(16,185,129,0); }
      }`}</style>

      {erreur && <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '12px', borderRadius: '8px', marginBottom: '14px' }}>{erreur}</div>}

      <div style={{ display: 'flex', background: '#F3F4F6', borderRadius: '9px', padding: '2.5px', marginBottom: '22px' }}>
        <button onClick={() => setOnglet('actifs')} style={ongletBtn(onglet === 'actifs')}>En cours / À venir</button>
        <button onClick={() => setOnglet('termines')} style={ongletBtn(onglet === 'termines')}>Terminés</button>
      </div>

      {liste.length === 0 ? (
        <div style={{ background: '#fff', border: '1px solid #F3F4F6', borderRadius: '12px', textAlign: 'center', color: '#9CA3AF', padding: '32px', fontSize: '14px' }}>
          {onglet === 'actifs' ? 'Aucun chantier à venir' : 'Aucun chantier terminé'}
        </div>
      ) : liste.map((c, i) => {
        const ouvrable = estAccessible(c);
        const enCours = c.etape !== 'planifie' && c.etape !== 'termine';
        const dernier = i === liste.length - 1;
        const rang = Math.max(ORDRE.indexOf(c.etape), 0);
        const etapeLib = ETAPES[rang] ? (ETAPES[rang].enCours || ETAPES[rang].label) : c.etape_libelle;

        return (
          <div key={c.id} style={{ display: 'flex', gap: '16px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '7px' }}>
              <div style={{
                width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0,
                background: c.date_prevue === auj ? '#10B981' : '#D1D5DB',
                animation: c.date_prevue === auj ? 'pulseJalon 2.4s ease-out infinite' : 'none',
                border: 'none'
              }} />
              <div style={{ width: '1px', flex: 1, minHeight: '30px', margin: '8px 0', background: c.date_prevue === auj ? 'linear-gradient(to bottom, #10B981, #E5E7EB)' : '#E5E7EB' }} />
            </div>

            <div style={{ flex: 1, paddingBottom: dernier ? 0 : '26px', minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '9px', gap: '10px', padding: '0 2%' }}>
                <span style={{ fontSize: '11px', letterSpacing: '0.4px', color: enCours ? '#065F46' : '#9CA3AF' }}>
                  {enCours ? 'EN COURS' : dateCourte(c.date_prevue)}
                </span>
                <span style={{ fontSize: '11px', color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                  {HORAIRES_LIB[c.creneau] || HORAIRES_LIB.journee}
                </span>
              </div>

              <div style={{
                background: '#fff', border: '1px solid #F3F4F6', borderRadius: '12px', padding: '17px',
                boxShadow: enCours ? '0 1px 2px rgba(0,0,0,0.04)' : 'none'
              }}>
                <div style={{ fontSize: '17px', fontWeight: 500, letterSpacing: '-0.2px', color: ouvrable ? '#111827' : '#6B7280' }}>
                  {c.client ? `${c.client.prenom} ${c.client.nom}` : 'Client'}
                </div>
                <div style={{ fontSize: '12.5px', color: '#9CA3AF', margin: '4px 0 16px' }}>
                  {c.client?.ville} {c.client?.code_postal}
                  {c.puissance_kwc ? ` · ${c.puissance_kwc} kWc` : ''}
                  {c.nb_panneaux ? ` · ${c.nb_panneaux} panneaux` : ''}
                </div>

                {ouvrable ? (
                  <>
                    <div style={{ display: 'flex', gap: '3px', marginBottom: '7px' }}>
                      {ETAPES.map((e, k) => (
                        <div key={e.cle} style={{ flex: 1, height: '3px', borderRadius: '999px', background: k < rang ? '#10B981' : '#E5E7EB' }} />
                      ))}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6B7280', marginBottom: '16px' }}>
                      <span>{c.etape === 'termine' ? 'Chantier terminé' : etapeLib}</span>
                      <span style={{ color: '#9CA3AF' }}>{rang} / {ETAPES.length}</span>
                    </div>
                    <button onClick={() => { setErreur(''); setOuvert(c); }}
                      style={{ width: '100%', padding: '11px', border: 'none', borderRadius: '8px', background: '#10B981', color: '#fff', fontSize: '14px', cursor: 'pointer' }}>
                      Ouvrir le chantier
                    </button>
                  </>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '7px', paddingTop: '12px', borderTop: '1px solid #F3F4F6' }}>
                    <Lock size={13} color="#9CA3AF" style={{ flexShrink: 0 }} />
                    <span style={{ fontSize: '12px', color: '#9CA3AF' }}>Accessible dès le {veilleDe(c.date_prevue)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TechMesChantiers;
